import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import IssuerInvestmentVM from './IssuerInvestmentVM';
import { observer } from 'mobx-react';
import * as Roles from '../../Models/Security/Roles';
import IssuerInvestmentPagedLookup from '../../Models/IssuerInvestments/Lookups/IssuerInvestmentPagedLookup';
import IssuerInvestment from '../../Models/IssuerInvestments/IssuerInvestment';
import NewIssuerInvestmentModal from './NewIssuerInvestmentModal';
import InvestmentComponent from '../Investments/Components/InvestmentComponent';
import { EnumHelper, Misc, ModalUtils, Model, NumberUtils, Validation } from '@singularsystems/neo-core';
import { Catalogue } from "@singularsystems/neo-react-services";
import AssetTypeCatalogueEntry from "../Catalogue/GenericComponents/AssetTypeCatalogueEntry";
import NotesGrid from "../Components/NotesGrid";
import LiquidityProvider from '../../Models/InvestorInvestments/LiquidityProvider';
import InvestmentDividendsComponent from '../Investments/Components/InvestmentDividendsComponent';
import NoticePeriodCatalogueEntry from '../Catalogue/GenericComponents/NoticePeriodCatalogueEntry';
import IssuerView from '../../../Domain/Views/Issuer/IssuerView';
import InvestmentInterest from '../../Models/IssuerInvestments/InvestmentInterest';
import { isHidden } from '../../Models/Config/FieldConfig/FieldSetting.enum';
import LinkedInvestorInvestmentLookup from '../../Models/InvestorInvestments/Lookups/LinkedInvestorInvestmentLookup';
import InvestorInvestmentView from '../InvestorInvestments/InvestorInvestmentView';
import AccrualAdjustment from '../../Models/Investment/AccrualAdjustment';
import PipelineCatalogueEntry from '../Catalogue/Pipelines/PipelineCatalogueEntry';
import issuerInvestmentPipelines from '../../Models/IssuerInvestments/IssuerInvestmentPipeline';
import { LiquidityLadder } from '../../Models/IssuerInvestments/LiquidityLadder';
import RollbackAllocationsCommand from "../../Models/IssuerInvestments/Commands/RollbackAllocationsCommand";
import AuthorisationComponents from "../../Components/AuthorisationComponents";
import AuthoriseButton from "../../Components/AuthoriseButton";
import GLAccountTypeCatalogueEntry from '../Catalogue/GenericComponents/GLAccountTypeCatalogueEntry';
import ProductTypeCatalogueEntry from '../Catalogue/General/ProductTypeCatalogueEntry';
import QPTracking from '../../Models/IssuerInvestments/QPTracking';
import InvestorInvestmentLookup from '../../Models/InvestorInvestments/Lookups/InvestorInvestmentLookup';
import DistributionTypeCatalogueEntry from '../Catalogue/General/DistributionTypeCatalogueEntry';
import TextValues from '../../Services/TextValues';
import IssuerInvestmentTermsAcceptedDate from '../../Models/IssuerInvestments/IssuerInvestmentTermsAcceptedDate';


class IssuerInvestmentParams {
    public issuerInvestmentId = {};
    public issuerId = { isQuery: true };
}

@observer
export default class IssuerInvestmentView extends Views.ViewBase<IssuerInvestmentVM, IssuerInvestmentParams> {
    public static params = new IssuerInvestmentParams();

    constructor(props: unknown) {
        super("Issuer Investment", IssuerInvestmentVM, props);
    }

    protected async viewParamsUpdated() {

        if (this.viewParams.issuerInvestmentId.value) {
            if (this.viewParams.issuerInvestmentId.value === "new") {
                await this.viewModel.newInvestment();
                this.viewParams.issuerInvestmentId.description = "New Investment";
            } else {
                await this.viewModel.loadInvestment(this.viewParams.issuerInvestmentId);
            }
        } else {
            this.viewModel.issuerInvestment = null;

            if (this.viewParams.issuerId.value) {
                this.viewModel.setCriteriaIssuer(this.viewParams.issuerId.asNullableInt());
            }
        }
    }

    public render() {
        const viewModel = this.viewModel,
            canEdit = viewModel.authorisation.hasRole(Roles.Issuer.Edit);
        const fieldSettings = viewModel.dataCache.fieldConfig.issuerInvestmentFields;

        return (
            <div>
                {!viewModel.issuerInvestment &&
                    <div>
                        <Neo.Card title="Find Issuer Investment">
                            <Neo.Form model={viewModel.criteria} onSubmit={() => this.searchImmediate()}>

                                <Neo.GridLayout lg={3}>
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.issuerInvestmentId} label="Issuer Inv ID." />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.issuerName} />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.entityId} select={{ itemSource: viewModel.dataCache.entities }} label="Entity" />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.showInactive} label="Show inactive?" />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.showUnauthorised} label="Unauthorised Only?" />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.productTypeId} select={{ itemSource: viewModel.dataCache.productTypes }} />
                                </Neo.GridLayout>

                                <Neo.Button isSubmit hidden />
                            </Neo.Form>
                        </Neo.Card>

                        <Neo.Card>
                            <div style={{ textAlign: "right" }} className="mb-3">
                                <Neo.Button onClick={() => this.viewParams.issuerInvestmentId.value = "new"} hidden={!canEdit}>New Issuer Investment</Neo.Button>
                            </div>

                            {this.hasInitialised &&
                                <Neo.Pager pageManager={viewModel.pageManager}>
                                    <NeoGrid.Grid<IssuerInvestmentPagedLookup>>
                                        {(item, meta) => (
                                            <NeoGrid.Row>
                                                <NeoGrid.Column label="Issuer Investment ID." display={meta.issuerInvestmentId} width={100} />
                                                <NeoGrid.Column display={meta.issuerName} />
                                                <NeoGrid.Column display={meta.investmentName} hidden={isHidden(fieldSettings.investmentName)}/>
                                                <NeoGrid.Column display={meta.entity} />
                                                <NeoGrid.Column display={meta.amount} numProps={{ currencySymbol: "R", format: Misc.NumberFormat.CurrencyNoDecimals }} />
                                                <NeoGrid.Column display={meta.utilised} numProps={{ currencySymbol: "R", format: Misc.NumberFormat.CurrencyNoDecimals }} />
                                                <NeoGrid.Column display={meta.remaining} numProps={{ currencySymbol: "R", format: Misc.NumberFormat.CurrencyNoDecimals }} />
                                                <NeoGrid.Column display={meta.isAuthorised} />
                                                <NeoGrid.ButtonColumn
                                                    editButton={{ onClick: () => this.viewParams.issuerInvestmentId.value = item.issuerInvestmentId }} />
                                            </NeoGrid.Row>
                                        )}
                                    </NeoGrid.Grid>
                                </Neo.Pager>}
                        </Neo.Card>
                    </div>}

                <NewIssuerInvestmentModal issuerInvestmentVM={viewModel} viewParam={this.viewParams.issuerInvestmentId} />

                {viewModel.issuerInvestment && this.renderInvestment(viewModel.issuerInvestment)}

                <Neo.Modal
                    title="Rollback Share Allocations"
                    bindModel={viewModel.meta.rollbackAllocationsCommand}
                    acceptButton={{ text: "Rollback", onClick: () => viewModel.rollbackAllocations() }}>
                    {(command: RollbackAllocationsCommand) => (
                        <div>
                            <Neo.Alert variant="warning" isOutline>
                                Note: This action cannot be undone.
                            </Neo.Alert>

                            <Neo.FormGroup bind={command.meta.fromDate} footerText="Share allocations on and after this date will be deleted." />
                        </div>
                    )}
                </Neo.Modal>

                <AuthorisationComponents viewModel={this.viewModel.authorisationVM} />
            </div >
        );
    }

    public renderInvestment(issuerInvestment: IssuerInvestment) {
        const viewModel = this.viewModel;
        var issuerInvestmentDetail = issuerInvestment.issuerInvestmentDetail.meta;
        const fieldSettings = viewModel.dataCache.fieldConfig.issuerInvestmentFields;

        return (
            <Neo.Form model={issuerInvestment} onSubmit={() => viewModel.saveInvestment()} showSummaryModal validationDisplayMode={Validation.DisplayMode.Always}>

                <Neo.Card title={"Investment - " + issuerInvestment.lookup.issuerName}>

                    <div className="d-flex justify-content-between mb-4">
                        <Neo.Button icon="arrow-left" variant="light" onClick={() => this.viewParams.issuerInvestmentId.reset()}>Back</Neo.Button>

                        <div>
                            <Neo.Button icon="user-secret" className="me-2" variant="light" onClick={() => this.confirmSaveAndRedirect(issuerInvestment)}>Issuer Details</Neo.Button>
                            {!issuerInvestment.isNew &&
                                <Neo.Button
                                    icon={issuerInvestment.isPendingDelete ? "trash-restore" : "trash"}
                                    variant="danger"
                                    tooltip={issuerInvestment.isPendingDelete ? "Stop the investment from being removed when authorised." : "Mark the investment to be removed when authorised."}
                                    className="min-width me-2"
                                    disabled={!issuerInvestment.investment.canDelete}
                                    onClick={async () => await this.viewModel.setIssuerInvestmentPendingDelete(issuerInvestment)} />}
                            <AuthoriseButton
                                className="me-2"
                                viewModel={viewModel.authorisationVM}
                                currentRecord={issuerInvestment}
                                authoriseRoleName={Roles.IssuerInvestment.Authorise}
                                onDelete={() => {
                                    this.viewModel.pageManager.refreshData(true);
                                    this.viewParams.issuerInvestmentId.reset();
                                }} />
                            <Neo.Button icon="save" isSubmit pulse={issuerInvestment.readyToSave}>Save</Neo.Button>
                        </div>
                    </div>

                    <Neo.GridLayout>
                        <div>
                            <Neo.FormGroupInline label="Ref No." display={issuerInvestment.meta.issuerInvestmentId} />
                            <Neo.FormGroupInline display={issuerInvestment.lookup.meta.issuerName} />
                        </div>
                        <div>
                            <Neo.FormGroupInline display={issuerInvestment.meta.entityId} select={{ itemSource: viewModel.dataCache.entities }} />
                            {issuerInvestment.investorInvestmentId &&
                                <Neo.FormGroupInline label="Investor Investment" display={issuerInvestment.meta.investorInvestmentName} />}
                        </div>
                    </Neo.GridLayout>

                </Neo.Card>

                <Neo.TabContainer className="mt-3" selectedTab={viewModel.meta.selectedTab}>
                    <Neo.Tab header="Details">
                        <Neo.GridLayout lg={2}>
                            <div>
                                <Neo.Card title={"Reporting"}>
                                    <Catalogue.FormGroupInline bind={issuerInvestmentDetail.assetTypeId} catalogueType={AssetTypeCatalogueEntry} hidden={isHidden(fieldSettings.assetTypeId)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.certificateNumber} hidden={isHidden(fieldSettings.certificateNumber)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.distinctiveNumber} hidden={isHidden(fieldSettings.distinctiveNumber)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.signingDate} hidden={isHidden(fieldSettings.signingDate)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.ledgerCode} hidden={isHidden(fieldSettings.ledgerCode)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.sapglCode} hidden={isHidden(fieldSettings.sapglCode)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.businessUnitNo} hidden={isHidden(fieldSettings.businessUnitNo)}
                                        label={fieldSettings.businessUnitNoLabel ?? "Business Unit No."} />
                                    <Catalogue.FormGroupInline
                                        bind={issuerInvestmentDetail.glAccountTypeId}
                                        catalogueType={GLAccountTypeCatalogueEntry}
                                        hidden={isHidden(fieldSettings.glAccountType)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.subscriptionDate} hidden={isHidden(fieldSettings.subscriptionDate)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.migrationDate} hidden={isHidden(fieldSettings.migrationDate)} />
                                </Neo.Card>
                                <Neo.Card title={"Information"}>
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.investmentName} hidden={isHidden(fieldSettings.investmentName)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.investmentCode} hidden={isHidden(fieldSettings.investmentCode)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.isin} hidden={isHidden(fieldSettings.isin)} />
                                    <Neo.FormGroupInline bindContext={issuerInvestmentDetail.branchId} hidden={isHidden(fieldSettings.branchId)} >
                                        <Neo.AutoCompleteDropDown
                                            bind={issuerInvestmentDetail.branchId}
                                            items={viewModel.branchDataSource}
                                            displayMember="profitCentre"
                                            valueMember="branchId"
                                            option={item => <>
                                                <div style={{ flexBasis: 100 }}>{item.groupName}</div>
                                                <div style={{ flexBasis: 100 }}>{item.profitCentre}</div>
                                                <div style={{ flexBasis: 100 }}>{item.branchNo}</div>
                                            </>
                                            }
                                        />
                                    </Neo.FormGroupInline>
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.security} input={{ rows: 2 }} hidden={isHidden(fieldSettings.security)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.fixedCostOfFunds} hidden={isHidden(fieldSettings.fixedCostOfFunds)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.liquidityPremium} numProps={{ formatString: "##0.0000" }} appendText={'%'} hidden={isHidden(fieldSettings.liquidityPremium)} />
                                    <Catalogue.FormGroupInline
                                        bind={issuerInvestmentDetail.noticePeriodId}
                                        catalogueType={NoticePeriodCatalogueEntry}
                                        hidden={isHidden(fieldSettings.noticePeriodId)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.originalBalance} hidden={isHidden(fieldSettings.originalBalance)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.psdAsset} hidden={isHidden(fieldSettings.psdAsset)} />
                                    <Neo.FormGroupInline
                                        hidden={isHidden(fieldSettings.liquidityLadder)}
                                        bind={issuerInvestmentDetail.liquidityLadder}
                                        select={{ items: EnumHelper.asList(LiquidityLadder) }} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.ncc} numProps={{ formatString: "##0.0000" }} appendText={'%'} hidden={isHidden(fieldSettings.ncc)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.lpDeterminationDate} hidden={isHidden(fieldSettings.lpDeterminationDate)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.otherCost} hidden={isHidden(fieldSettings.otherCost)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.equityUpside}
                                        footerText="If ticked, the investment will show zero in certain columns of the superset file."
                                        hidden={isHidden(fieldSettings.equityUpside)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.capacityWarehouse} hidden={isHidden(fieldSettings.capacityWarehouse)} />
                                    <Neo.FormGroupInline bind={issuerInvestmentDetail.qualifyingPurpose} hidden={isHidden(fieldSettings.qualifyingPurpose)} />
                                    <Catalogue.FormGroupInline
                                        bind={issuerInvestmentDetail.productTypeId}
                                        catalogueType={ProductTypeCatalogueEntry}
                                        hidden={isHidden(fieldSettings.productTypeId)} />
                                    <Catalogue.FormGroupInline
                                        bind={issuerInvestmentDetail.distributionTypeId} catalogueType={DistributionTypeCatalogueEntry}
                                        hidden={isHidden(fieldSettings.distributionTypeId)} />
                                </Neo.Card>
                                <Neo.Card title="Participation Agreements" disabled={viewModel.issuerInvestment!.isNew} >
                                    <Neo.FileContext fileManager={viewModel.fileManager}>
                                        <Neo.FileDropArea>
                                            <Neo.FileUploadButton variant="primary" >Select participation agreement file</Neo.FileUploadButton> or drag a file here.
                                            <Neo.FileUploadProgress />
                                        </Neo.FileDropArea>
                                    </Neo.FileContext>
                                    {viewModel.issuerInvestment!.lookup.participationAgreements.length !== 0 &&
                                        <NeoGrid.Grid className="mt-3" items={viewModel.issuerInvestment!.lookup.participationAgreements}>
                                            {(participationAgreement, meta) => (
                                                <NeoGrid.Row>
                                                    <NeoGrid.Column display={meta.uploadedOn} />
                                                    <NeoGrid.Column display={meta.fileName} />
                                                    <NeoGrid.ButtonColumn
                                                        editButton={{
                                                            icon: "file",
                                                            variant: "link",
                                                            text: "View",
                                                            onClick: () => this.viewModel.downloadParticipationAgreementFile(participationAgreement.fileDescriptorId)
                                                        }} />
                                                    <NeoGrid.ButtonColumn deleteButton={{
                                                        onClick: () => this.viewModel.deleteParticipationAgreements(participationAgreement)
                                                    }} />
                                                </NeoGrid.Row>
                                            )}
                                        </NeoGrid.Grid>}
                                </Neo.Card>
                            </div>
                            <div>
                                <Neo.Card title={"Pipelines"}>
                                    <NeoGrid.Grid items={viewModel.issuerInvestment?.issuerInvestmentPipelines} addButton={{ size: "sm", text: "Link pipeline" }}>
                                        {(item: issuerInvestmentPipelines, meta) => (
                                            <NeoGrid.Row>
                                                <Catalogue.Column bind={meta.pipelineId} catalogueType={PipelineCatalogueEntry}>
                                                </Catalogue.Column>
                                                <NeoGrid.Column bind={meta.pipelinePercentage}
                                                    appendText={"%"}
                                                    numProps={{ formatString: "#,##0.00" }} />
                                                <NeoGrid.ButtonColumn showDelete />
                                            </NeoGrid.Row>
                                        )}
                                    </NeoGrid.Grid>
                                </Neo.Card>

                                <Neo.Card title={"Terms Accepted Dates"}>
                                    <NeoGrid.Grid items={viewModel.issuerInvestment?.termsAcceptedDates} showAddButton>
                                        {(item: IssuerInvestmentTermsAcceptedDate, meta) => (
                                            <NeoGrid.Row>
                                                <NeoGrid.Column bind={meta.investorReferralTypeId} select={{ itemSource: viewModel.dataCache.investorReferralTypes }} />
                                                <NeoGrid.Column bind={meta.termsAcceptedDate} />
                                                <NeoGrid.Column bind={meta.emailAddress} />
                                                <NeoGrid.ButtonColumn showDelete />
                                            </NeoGrid.Row>
                                        )}
                                    </NeoGrid.Grid>
                                </Neo.Card>

                                <Neo.Card title="Notes">
                                    <NotesGrid parentModel={viewModel.issuerInvestment!} viewModel={viewModel.noteVM} />
                                </Neo.Card>
                            </div>
                        </Neo.GridLayout>
                    </Neo.Tab>
                    <Neo.Tab header="Investment">
                        <InvestmentComponent
                            investmentHost={issuerInvestment}
                            apiClient={this.viewModel.issuerInvestmentsApiClient}
                            taskRunner={this.taskRunner}
                            onTransactionChange={() => this.viewModel.pageManager.refreshPage()}
                            fieldSettings={fieldSettings}
                            additionalCalcOptions={
                            <Neo.FormGroup
                                bind={issuerInvestment.investment.meta.reinvestDividends}
                                hidden={isHidden(fieldSettings.reinvestDividends)}
                                label={`Recapitalise ${TextValues.distribution}s?`}
                                footerText={`${TextValues.distribution}s are recapitalised into the investment as a new capital transaction.`} />
                            }
                        />
                    </Neo.Tab>
                    <Neo.Tab header={`${TextValues.distribution}s`}>
                        <InvestmentDividendsComponent investmentHost={issuerInvestment} taskRunner={this.taskRunner} />
                    </Neo.Tab>
                    {issuerInvestment.lookup.hasVestingTrust && <Neo.Tab header="Liquidity Providers" onDisplayed={() => viewModel.fetchLiquidityProviders()}>
                        <div className="text-end mb-3">
                            <Neo.Button icon="save" onClick={() => viewModel.saveLiquidityProvider()} pulse={viewModel.liquidityProviderList.isDirty} disabled={issuerInvestment.isNew}>Save</Neo.Button>
                        </div>
                        <NeoGrid.Grid items={viewModel.liquidityProviderList} disabled={issuerInvestment.isNew} showAddButton={!issuerInvestment.isNew}>
                            {(item: LiquidityProvider, meta) => (
                                <NeoGrid.Row>
                                    <NeoGrid.Column bindContext={meta.investorInvestmentId} label="Investor Investment">
                                        <Neo.AutoCompleteDropDown
                                            bind={meta.investorInvestmentId}
                                            bindDisplay={meta.investorInvestmentName}
                                            itemSource={(st) => viewModel.investorInvestmentApiClient.getInvestorInvestmentLookups(st, null, issuerInvestment.issuerInvestmentId)} />
                                    </NeoGrid.Column>
                                    <NeoGrid.Column bind={meta.shortName} />
                                    <NeoGrid.Column bind={meta.buyPriority} />
                                    <NeoGrid.Column bind={meta.sellPriority} />
                                    <NeoGrid.Column bind={meta.maxHoldingPercent} appendText="%" />
                                    <NeoGrid.ButtonColumn showDelete />
                                </NeoGrid.Row>
                            )}
                        </NeoGrid.Grid>
                    </Neo.Tab>}
                    <Neo.Tab header="Linked Investments" onInitialise={() => viewModel.getLinkedInvestorInvestments()}>

                        {issuerInvestment.lastAllocationDate &&
                            <Neo.Card title="Share Allocations" className="mb-3">
                                <Neo.GridLayout>
                                    <div>
                                        <Neo.FormGroupInline
                                            display={issuerInvestment.meta.lastAllocationDate}
                                            footerText="The latest date on which share allocations were generated for this issuer investment."
                                            append={<Neo.Button
                                                variant="warning"
                                                onClick={() => viewModel.beginRollbackAllocations()}
                                                icon="undo"
                                                tooltip="Rollback share allocations if you need to insert backdated transactions." />} />
                                    </div>
                                    <div>
                                    </div>
                                </Neo.GridLayout>
                            </Neo.Card>}

                        <NeoGrid.Grid<LinkedInvestorInvestmentLookup> items={viewModel.linkedInvestorInvestments} >
                            {(linkedInvestment, meta) => (
                                <NeoGrid.Row>
                                    <NeoGrid.Column display={meta.investorInvestmentId} label="Ref No." />
                                    <NeoGrid.Column display={meta.investor} />
                                    <NeoGrid.Column display={meta.entityId} select={{ itemSource: viewModel.dataCache.entities }} label="Entity" />
                                    <NeoGrid.Column display={meta.investmentEndDate} />
                                    <NeoGrid.Column display={meta.noOfShares} />
                                    <NeoGrid.Column display={meta.amount} />
                                    <NeoGrid.ButtonColumn
                                        viewButton={{
                                            icon: "coins", text: "View", variant: "light", tooltip: "View investment in a new tab",
                                            onClick: () => this.confirmSaveAndRedirect(issuerInvestment, linkedInvestment.investorInvestmentId)
                                        }} />
                                </NeoGrid.Row>
                            )}
                        </NeoGrid.Grid>
                    </Neo.Tab>

                    <Neo.Tab header="QP Tracking" onInitialise={() => viewModel.fetchQPTracking()}>
                        <NeoGrid.Grid<QPTracking> items={issuerInvestment.qpTracking} addButton={{
                            onClick: () => {
                                var qpTracking = issuerInvestment.qpTracking.addNew();
                                qpTracking.issuerInvestmentId = issuerInvestment.issuerInvestmentId;
                                qpTracking.qpGroupId = issuerInvestment.issuerInvestmentId;
                            }
                        }} >
                            {(qpTracking, meta) => (
                                <NeoGrid.Row>
                                    <NeoGrid.Column bind={meta.qpGroupId} label="QP Group" />
                                    <NeoGrid.Column bindContext={meta.investorInvestmentId} >
                                        <Neo.AutoCompleteDropDown
                                            bind={meta.investorInvestmentId}
                                            bindDisplay={meta.investorInvestmentName}
                                            itemSource={(st) => viewModel.investorInvestmentApiClient.getInvestorInvestmentLookups(st, false, null)}
                                            option={(item: Model.PlainObject<InvestorInvestmentLookup>) => <>
                                                <div style={{ flexBasis: 100 }}>{item.investorName}</div>
                                                <div style={{ flexBasis: 100 }}>{item.investorInvestmentId}</div>
                                                <div style={{ flexBasis: 100 }}>
                                                    {NumberUtils.format(item.currentBalance, Misc.NumberFormat.CurrencyNoDecimals, "R")}
                                                </div>
                                            </>
                                            }
                                            onItemSelected={(item) => {
                                                qpTracking.investorInvestmentName = item != null ? item.investorInvestmentId + " - " + item.investorName : "";
                                                qpTracking.investorInvestmentCurrentBalance = item?.currentBalance ?? 0;
                                            }}
                                        />
                                    </NeoGrid.Column>
                                    <NeoGrid.Column bind={meta.investorInvestmentCurrentBalance} label="Current Balance" disabled numProps={{ currencySymbol: "R" }} sum />
                                    <NeoGrid.Column bind={meta.amount} numProps={{ currencySymbol: "R" }} sum />
                                    <NeoGrid.Column bind={meta.startDate} />
                                    <NeoGrid.Column bind={meta.endDate} />
                                    <NeoGrid.ButtonColumn showDelete />
                                </NeoGrid.Row>
                            )}
                        </NeoGrid.Grid>
                    </Neo.Tab>

                    <Neo.Tab header="Accrual Adjustments">
                        <NeoGrid.Grid<AccrualAdjustment> items={issuerInvestment.investment.accrualAdjustments} showAddButton >
                            {(accrualAdjustments, meta) => (
                                <NeoGrid.Row>
                                    <NeoGrid.Column bind={meta.adjustmentDate} />
                                    <NeoGrid.Column bind={meta.amount} width="50%" />
                                    <NeoGrid.ButtonColumn showDelete />
                                </NeoGrid.Row>
                            )}
                        </NeoGrid.Grid>
                    </Neo.Tab>
                    <Neo.Tab header="Investment Interest">
                        <NeoGrid.Grid<InvestmentInterest> items={issuerInvestment.investmentInterestList} showAddButton >
                            {(investmentInterest, meta) => (
                                <NeoGrid.Row>
                                    <NeoGrid.Column bind={meta.interestDate} width="50%" />
                                    <NeoGrid.Column bind={meta.interest} />
                                    <NeoGrid.ButtonColumn showDelete />
                                </NeoGrid.Row>
                            )}
                        </NeoGrid.Grid>
                    </Neo.Tab>
                </Neo.TabContainer>

            </Neo.Form >
        )
    }

    private async searchImmediate() {
        if (this.viewModel.searchAutoRunner.cancelPending() || !this.viewModel.pageManager.hasFetched) {
            await this.viewModel.pageManager.refreshData();
        }

        if (this.viewModel.pageManager.data.length === 1) {
            this.viewParams.issuerInvestmentId.value = this.viewModel.pageManager.data[0].issuerInvestmentId;
        }
    }

    private async confirmSaveAndRedirect(issuerInvestment: IssuerInvestment, investorInvestmentId: number | null = null) {
        if (!investorInvestmentId) {
            if (issuerInvestment.readyToSave && await ModalUtils.showYesNoDismissible('save', `Would you like to save the changes that were made to this issuer?`) === Misc.ModalResult.Yes) {
                await this.viewModel.saveInvestment();
            }

            this.navigation.navigateToView(IssuerView, { issuerId: issuerInvestment.issuerId });
        }
        else {
            window.open(this.navigation.getPathForView(InvestorInvestmentView, { investorInvestmentId: investorInvestmentId }), "_tab", "width=1200,height=960");
        }
    }

    public onLeave(): string | undefined {
        if (this.viewModel.issuerInvestment?.isDirty) {
            return "Are you sure you want to leave this page without saving?";
        }
        return undefined;
    }
}